import { useEffect, useRef, useState } from "react";
import type { OverlayManager } from "../overlay/useOverlayManager";

export interface OverlayMenuProps {
  overlays: { id: string; name: string; inherited?: true }[];
  visibleIds: Set<string>;
  onToggle: (id: string, visible: boolean) => void;
  /** Adding and removing (spec 2026-09-09). Absent → toggles only. */
  manager?: OverlayManager;
}

const OFFLINE_TITLE = "Needs a connection";

export function OverlayMenu({ overlays, visibleIds, onToggle, manager }: OverlayMenuProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const pending = manager?.pending ?? null;

  // A drop with the menu closed still needs its name asked for.
  useEffect(() => {
    if (pending !== null) setOpen(true);
  }, [pending]);

  const canManage = manager !== undefined && manager.online;

  return (
    <div style={{ position: "relative" }}>
      <button
        type="button"
        className="vmc-tbtn"
        data-tour="viewer.overlays"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {`Overlays · ${overlays.length} ▾`}
      </button>
      {open && (
        <div role="menu" className="vmc-export-menu vmc-overlay-menu">
          {pending !== null && manager !== undefined ? (
            <div className="vmc-overlay-pending">
              <div className="vmc-overlay-pending-file">
                <span>{pending.filename}</span>
                <small>{`${pending.featureCount} feature${pending.featureCount === 1 ? "" : "s"}`}</small>
              </div>
              <label htmlFor="vmc-overlay-name">Name</label>
              <input
                id="vmc-overlay-name"
                type="text"
                value={pending.name}
                maxLength={80}
                disabled={pending.busy}
                autoFocus
                onChange={(e) => manager.setName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") void manager.confirm(); }}
              />
              <small className="vmc-overlay-note">
                {pending.replaces
                  ? `This will replace the overlay called "${pending.name.trim()}".`
                  : "Shows on every flight for this team."}
              </small>
              {pending.error !== null && (
                <div role="alert" className="vmc-overlay-error">{pending.error}</div>
              )}
              <div className="vmc-overlay-row">
                <button
                  type="button"
                  className="vmc-tbtn"
                  disabled={pending.busy || pending.name.trim().length === 0}
                  onClick={() => { void manager.confirm(); }}
                >
                  {pending.busy ? "Adding…" : pending.replaces ? "Replace" : "Add"}
                </button>
                <button type="button" className="vmc-tbtn" disabled={pending.busy} onClick={manager.cancel}>
                  Cancel
                </button>
                {manager.queued > 0 && <small>{`${manager.queued} more waiting`}</small>}
              </div>
            </div>
          ) : (
            <>
              {overlays.length === 0 && (
                <div className="vmc-export-menu-item vmc-overlay-empty"><em>No overlays yet.</em></div>
              )}
              {overlays.map((o) => (
                <div key={o.id} className="vmc-export-menu-item vmc-overlay-row">
                  {confirming === o.id ? (
                    <>
                      <span>{`Remove "${o.name}"?`}</span>
                      <button
                        type="button"
                        className="vmc-tbtn vmc-danger"
                        onClick={() => { setConfirming(null); void manager?.remove(o.id); }}
                      >
                        Remove
                      </button>
                      <button type="button" className="vmc-tbtn" onClick={() => setConfirming(null)}>
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <label style={{ display: "flex", gap: 8, alignItems: "center", flex: 1 }}>
                        <input
                          type="checkbox"
                          checked={visibleIds.has(o.id)}
                          onChange={(e) => onToggle(o.id, e.target.checked)}
                        />
                        {o.name}
                      </label>
                      {o.inherited === true ? (
                        <small className="vmc-overlay-inherited">shared with you</small>
                      ) : manager !== undefined && (
                        <button
                          type="button"
                          className="vmc-overlay-remove"
                          aria-label={`Remove ${o.name}`}
                          title={canManage ? `Remove ${o.name}` : OFFLINE_TITLE}
                          disabled={!canManage}
                          onClick={() => setConfirming(o.id)}
                        >
                          ×
                        </button>
                      )}
                    </>
                  )}
                </div>
              ))}
              {manager !== undefined && (
                <div className="vmc-export-menu-item vmc-overlay-row">
                  <button
                    type="button"
                    className="vmc-tbtn"
                    disabled={!canManage}
                    title={canManage ? "Add a KMZ or KML from your computer" : OFFLINE_TITLE}
                    onClick={() => fileInput.current?.click()}
                  >
                    Add KMZ…
                  </button>
                  <input
                    ref={fileInput}
                    data-testid="overlay-file-input"
                    type="file"
                    accept=".kmz,.kml"
                    multiple
                    hidden
                    onChange={(e) => {
                      const files = Array.from(e.target.files ?? []);
                      e.target.value = "";
                      if (files.length > 0) manager.addFiles(files);
                    }}
                  />
                </div>
              )}
            </>
          )}
          {manager?.notice != null && (
            <div role="status" className="vmc-export-menu-item vmc-overlay-notice">
              <span>{manager.notice}</span>
              <button type="button" className="vmc-overlay-remove" aria-label="Dismiss" onClick={manager.dismissNotice}>
                ×
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
